import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '@/lib/api';
import {
  CampaignDispositions,
  type CampaignDispositionsData,
} from '@/components/CampaignDispositions';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PageHeader,
  Select,
  Toast,
} from '@/components/ui';
import { formatNumber } from '@/lib/format';

interface Block extends CampaignDispositionsData {
  tenant_id: string;
  client_name: string;
  client_slug: string;
}

interface Response {
  window_days: number;
  data: Block[];
}

interface ClientOption {
  id: string;
  name: string;
}

/**
 * Vicidial dispositions, per client and per dialer.
 *
 * The per-dialer split is the point. Two dialers legitimately run a campaign
 * with the same id, and a disposition written back to the wrong one is a silent
 * failure — the write succeeds against a lead that happens to exist on both.
 * Seeing them apart is how you notice.
 */
export default function Dispositions() {
  const [days, setDays] = useState(30);
  const [tenantId, setTenantId] = useState('');

  const clients = useQuery({
    queryKey: ['admin-clients-select'],
    queryFn: () => api.get<{ data: ClientOption[] }>('/admin/clients', { page_size: 200 }),
    staleTime: 60_000,
  });

  const query = useQuery({
    queryKey: ['admin-dispositions', days, tenantId],
    queryFn: () =>
      api.get<Response>('/admin/dispositions', {
        days,
        tenant_id: tenantId || undefined,
      }),
  });

  return (
    <div className="w-full">
      <PageHeader
        title="Dispositions"
        description="What went back to Vicidial, per client and per dialer."
        actions={
          <div className="flex gap-2">
            <Select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="w-auto"
            >
              <option value="">All clients</option>
              {(clients.data?.data ?? []).map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
            <Select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="w-auto"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </Select>
          </div>
        }
      />

      <DispositionMapEditor tenantId={tenantId} />

      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} retry={() => query.refetch()} />
      ) : query.data.data.length === 0 ? (
        <Card>
          <EmptyState
            title="No dispositions yet"
            description="These appear once calls have been dispositioned back to a dialer."
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {query.data.data.map((block) => (
            <div key={block.tenant_id}>
              <div className="mb-2 flex items-baseline justify-between gap-3">
                <Link
                  to={`/clients/${block.tenant_id}`}
                  className="text-base font-semibold text-ink-900 hover:text-brand-600 dark:text-ink-50 dark:hover:text-brand-400"
                >
                  {block.client_name}
                </Link>
                <span className="text-xs tabular-nums text-ink-500">
                  {formatNumber(block.calls)} call{block.calls === 1 ? '' : 's'}
                </span>
              </div>
              <CampaignDispositions
                data={block}
                title="By campaign and dialer"
                description="Percentages are of each campaign's own volume."
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface StatusRow {
  code: string;
  label: string;
  group: string;
  description: string;
  settable: boolean;
  human: boolean;
  needs_setup: boolean;
}

interface OutcomeRow {
  key: string;
  label: string;
  default_code: string;
}

interface Catalogue {
  statuses: StatusRow[];
  outcomes: OutcomeRow[];
}

interface DialerServer {
  id: string;
  name: string;
}

interface MapResponse {
  integration_id: string;
  name: string;
  defaults: Record<string, string>;
  overrides: Record<string, string>;
  effective: Record<string, string>;
}

const GROUP_LABEL: Record<string, string> = {
  AGENT: 'Agent dispositions',
  SYSTEM: 'System statuses',
  SURVEY: 'Survey and IVR',
  CUSTOM: 'This platform',
};

/**
 * Which Vicidial status this platform writes for each outcome it can decide.
 *
 * Per dialer, because the codes are. One client's transfer is `XFER` and
 * another's is `SALE`; a dialer with no `DAIR` status needs dead air pointed
 * somewhere it does have.
 *
 * Only statuses the platform may write are offered. The rest are Vicidial
 * describing its own live state — `INCALL` on a call that has ended, or
 * `NEW`, which would put the lead back in the dialling pool to be called
 * again and again. The server refuses them too; this is so nobody has to find
 * that out from an error.
 */
function DispositionMapEditor({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [serverId, setServerId] = useState('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const servers = useQuery({
    queryKey: ['vicidial-servers', tenantId],
    queryFn: () =>
      api.get<{ data: DialerServer[] }>('/vicidial-integrations', {
        tenant_id: tenantId,
        page_size: 100,
      }),
    enabled: Boolean(tenantId),
  });

  const catalogue = useQuery({
    queryKey: ['vicidial-statuses'],
    queryFn: () => api.get<Catalogue>('/admin/vicidial/statuses'),
    staleTime: 5 * 60_000,
  });

  const current = useQuery({
    queryKey: ['vicidial-dispositions', serverId],
    queryFn: () =>
      api.get<MapResponse>(`/admin/vicidial/servers/${serverId}/dispositions`),
    enabled: Boolean(serverId),
  });

  useEffect(() => {
    if (current.data) setDraft({ ...current.data.overrides });
  }, [current.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/admin/vicidial/servers/${serverId}/dispositions`, {
        dispositions: draft,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vicidial-dispositions', serverId] });
      setToast({ message: 'Saved. New calls will use these codes.', tone: 'good' });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not save'), tone: 'bad' }),
  });

  if (!tenantId) {
    return (
      <Card title="Outcome codes" className="mb-6">
        <p className="text-sm text-ink-500">
          Choose a client above to edit which Vicidial status this platform
          writes for each outcome.
        </p>
      </Card>
    );
  }

  const settable = (catalogue.data?.statuses ?? []).filter((s) => s.settable);
  const groups = ['AGENT', 'SYSTEM', 'SURVEY', 'CUSTOM'].filter((g) =>
    settable.some((s) => s.group === g),
  );
  const byCode = new Map(settable.map((s) => [s.code, s]));

  return (
    <Card
      title="Outcome codes"
      description="Which Vicidial status goes back for each outcome the bot can decide. Per dialer, because the codes are."
      className="mb-6"
    >
      <div className="mb-4 max-w-sm">
        <Field label="Dialer">
          <Select value={serverId} onChange={(e) => setServerId(e.target.value)}>
            <option value="">Choose a dialer…</option>
            {(servers.data?.data ?? []).map((server) => (
              <option key={server.id} value={server.id}>
                {server.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {!serverId ? null : current.isPending || catalogue.isPending ? (
        <Loading label="Reading the map…" />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="glee-table glee-table--compact">
              <thead>
                <tr>
                  <th>Outcome</th>
                  <th>Vicidial status</th>
                  <th>Means</th>
                </tr>
              </thead>
              <tbody>
                {(catalogue.data?.outcomes ?? []).map((outcome) => {
                  const chosen = draft[outcome.key] ?? '';
                  const effective = chosen || outcome.default_code;
                  const status = byCode.get(effective);
                  return (
                    <tr key={outcome.key}>
                      <td>
                        <div className="font-medium">{outcome.label}</div>
                        <div className="text-xs text-ink-500">{outcome.key}</div>
                      </td>
                      <td>
                        <Select
                          value={chosen}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, [outcome.key]: e.target.value }))
                          }
                          className="w-56"
                        >
                          {/* Empty is not "no code" — it is the platform
                              default, which is what most rows should stay on
                              and what clearing a row goes back to. */}
                          <option value="">
                            Default ({outcome.default_code})
                          </option>
                          {groups.map((group) => (
                            <optgroup key={group} label={GROUP_LABEL[group] ?? group}>
                              {settable
                                .filter((s) => s.group === group)
                                .map((s) => (
                                  <option key={s.code} value={s.code}>
                                    {s.code} — {s.label}
                                  </option>
                                ))}
                            </optgroup>
                          ))}
                        </Select>
                      </td>
                      <td className="text-xs text-ink-500">
                        {status?.description ?? '—'}
                        {status?.needs_setup && (
                          <div className="mt-1 text-amber-700 dark:text-amber-300">
                            Not a stock Vicidial status. Create it in the Vicidial
                            admin first, or this disposition will not stick.
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Save codes
            </Button>
            <Button variant="ghost" onClick={() => setDraft({})}>
              Reset all to default
            </Button>
          </div>

          <p className="mt-3 text-xs text-ink-500">
            Vicidial&apos;s own live states — <code>NEW</code>, <code>INCALL</code>,{' '}
            <code>QUEUE</code>, <code>PARK</code> and the rest — are deliberately
            not offered. They describe what the dialer is doing, not how a call
            ended, and <code>NEW</code> in particular would put the lead back in
            the pool to be dialled again.
          </p>
        </>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </Card>
  );
}

